import { observable, type IObservableValue } from 'mobx';

/**
 * A single observable form-field value, wrapped so an editable entity can
 * expose it through a plain getter/setter pair:
 *
 * ```ts
 * private readonly _name = new Property<string | null>(null);
 * get name(): string | null { return this._name.get(); }
 * setName = this._name.set; // bound — alias directly, no re-wrap
 * ```
 *
 * The box is non-deep: the held value is treated as an opaque, replace-on-
 * write unit (primitive, or an immutable reference object like
 * `ZoneReference`). MobX never proxies into it, so reads observe the field
 * and writes replace it wholesale — no per-key reactivity surprises.
 *
 * `set` is a bound lambda so an entity can alias it straight onto a public
 * setter (above) or hand it to a component as an `onChange` without losing
 * `this`. `get` stays a method: reads are exposed through a re-reading
 * getter, never passed by reference, so binding would buy nothing.
 *
 * Today it is a thin wrapper over `IObservableValue<T>`. It exists as its own
 * type so field-level concerns that don't belong on the entity (mandatory
 * flags, validity checks, original-value tracking) have a home to grow into
 * without touching every accessor.
 */
export class Property<T> {
  private readonly _value: IObservableValue<T>;

  constructor(initial: T) {
    this._value = observable.box(initial, { deep: false });
  }

  get(): T {
    return this._value.get();
  }

  set = (value: T): void => {
    this._value.set(value);
  };
}
